"use client"

import { useState, useEffect, useCallback } from "react"
import {
  api,
  APIError,
  type CategoryCreate,
  type CategoryOut,
  type AdminStatistics,
  type LanguageOut,
  type QuestionCreate,
  type QuestionOut,
  type UserBase, // Import UserBase
  type UserUpdate, // Import UserUpdate
} from "@/lib/api"
import { toast } from "sonner"

// Custom hook for API data fetching with loading states
export function useAPIData() {
  const [categories, setCategories] = useState<CategoryOut[]>([])
  const [questions, setQuestions] = useState<QuestionOut[]>([])
  const [languages, setLanguages] = useState<LanguageOut[]>([])
  const [users, setUsers] = useState<UserBase[]>([]) // New state for users
  const [adminStats, setAdminStats] = useState<AdminStatistics | null>(null)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const getQuestionById = useCallback(async (questionId: number): Promise<QuestionOut | null> => {
    try {
      const question = await api.questions.one(questionId)
      return question
    } catch (err) {
      const errorMessage = err instanceof APIError ? err.message : "Failed to load question"
      setError(errorMessage)
      toast.error("Error loading question", {
        description: errorMessage,
      })
      return null
    }
  }, [])

  const getUserById = useCallback(async (userId: number): Promise<UserBase | null> => {
    try {
      const user = await api.users.one(userId)
      return user
    } catch (err) {
      const errorMessage = err instanceof APIError ? err.message : "Failed to load user"
      setError(errorMessage)
      toast.error("Error loading user", {
        description: errorMessage,
      })
      return null
    }
  }, [])

  // Load initial data
  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const [categoriesData, questionsData, languagesData, usersData, adminStatsData] = await Promise.all([
        api.categories.list(),
        api.questions.list(),
        api.languages.list(),
        api.users.list(), // Fetch users
        api.dashboard.statistics(),
      ])

      setCategories(categoriesData)
      setQuestions(questionsData)
      setLanguages(languagesData)
      setUsers(usersData) // Set users data
      setAdminStats(adminStatsData)
    } catch (err) {
      const errorMessage = err instanceof APIError ? err.message : "Failed to load data"
      setError(errorMessage)
      toast.error("Error loading data", {
        description: errorMessage,
      })
    } finally {
      setLoading(false)
    }
  }, [])

  // Create category
  const createCategory = async (categoryData: CategoryCreate): Promise<CategoryOut | null> => {
    try {
      const newCategory = await api.categories.create(categoryData)
      await loadData() // Reload all data to ensure consistency
      toast.success("Category created", {
        description: "New category has been successfully created.",
      })
      return newCategory
    } catch (err) {
      const errorMessage = err instanceof APIError ? err.message : "Failed to create category"
      toast.error("Error creating category", {
        description: errorMessage,
      })
      return null
    }
  }

  // Create question
  const createQuestion = async (questionData: QuestionCreate): Promise<QuestionOut | null> => {
    try {
      const newQuestion = await api.questions.create(questionData)
      await loadData() // Reload all data
      toast.success("Question created", {
        description: "New question has been successfully created.",
      })
      return newQuestion
    } catch (err) {
      const errorMessage = err instanceof APIError ? err.message : "Failed to create question"
      toast.error("Error creating question", {
        description: errorMessage,
      })
      return null
    }
  }

  const updateCategory = async (categoryId: number, updatedData: Partial<CategoryCreate>): Promise<CategoryOut | null> => {
    try {
      const updatedCategory = await api.categories.update(categoryId, updatedData)
      await loadData() // Reload to reflect changes if API updated
      toast.success("Category updated", {
        description: "Category has been successfully updated.",
      })
      return updatedCategory
    } catch (err) {
      const errorMessage = err instanceof APIError ? err.message : "Failed to update category"
      toast.error("Error updating category", {
        description: errorMessage,
      })
      return null
    }
  }

  const uploadCategoryPhoto = async (
    categoryId: number,
    file: File,
  ): Promise<{ id: number; url: string } | null> => {
    try {
      const photo = await api.categories.uploadPhoto(categoryId, file)
      await loadData()
      toast.success("Category image uploaded", {
        description: "The category image has been updated.",
      })
      return photo
    } catch (err) {
      const errorMessage = err instanceof APIError ? err.message : "Failed to upload category image"
      toast.error("Error uploading image", {
        description: errorMessage,
      })
      return null
    }
  }

  // Update question (Placeholder if API doesn't fully support. For now, it'll just toast.)
  const updateQuestion = async (questionId: number, updatedData: Partial<QuestionCreate>) => {
    try {
      // Assuming an update endpoint exists, or this would be local state update
      // await api.questions.update(questionId, updatedData);
      await loadData() // Reload to reflect changes if API updated
      toast.success("Question updated", {
        description: "Question has been successfully updated.",
      })
    } catch (err) {
      const errorMessage = err instanceof APIError ? err.message : "Failed to update question"
      toast.error("Error updating question", {
        description: errorMessage,
      })
    }
  }

  // Update user
  const updateUser = async (userId: number, updatedData: UserUpdate) => {
    try {
      await api.users.update(userId, updatedData)
      await loadData() // Reload all data
      toast.success("User updated", {
        description: "User details have been successfully updated.",
      })
    } catch (err) {
      const errorMessage = err instanceof APIError ? err.message : "Failed to update user"
      toast.error("Error updating user", {
        description: errorMessage,
      })
    }
  }

  // Delete category
  const deleteCategory = async (categoryId: number) => {
    try {
      await api.categories.remove(categoryId)
      await loadData() // Reload all data
      toast.success("Category deleted", {
        description: "Category and its associated questions have been deleted.",
      })
    } catch (err) {
      const errorMessage = err instanceof APIError ? err.message : "Failed to delete category"
      toast.error("Error deleting category", {
        description: errorMessage,
      })
    }
  }

  // Delete question
  // const deleteQuestion = async (questionId: string) => {
  //   try {
  //     await api.questions.remove(Number(questionId))
  //     await loadData() // Reload all data
  //     toast.success("Question deleted", {
  //       description: "Question has been successfully deleted.",
  //     })
  //   } catch (err) {
  //     const errorMessage = err instanceof APIError ? err.message : "Failed to delete question"
  //     toast.error("Error deleting question", {
  //       description: errorMessage,
  //     })
  //   }
  // }

  // Delete user
  const deleteUser = async (userId: number) => {
    try {
      await api.users.remove(userId)
      await loadData() // Reload all data
      toast.success("User deleted", {
        description: "User has been successfully deleted.",
      })
    } catch (err) {
      const errorMessage = err instanceof APIError ? err.message : "Failed to delete user"
      toast.error("Error deleting user", {
        description: errorMessage,
      })
    }
  }

  // const genQuestion = async (category: string) => {
  //   try {
  //     return await api.genQuestion(category)
  //   } catch (err) {
  //     const errorMessage = err instanceof APIError ? err.message : "Failed to generate question"
  //     toast.error("Error generating question", {
  //       description: errorMessage,
  //     })
  //     throw err // Re-throw to be handled by the component
  //   }
  // }

  const refreshData = async () => {
    await loadData()
  }

  // Load data on mount
  useEffect(() => {
    loadData()
  }, [loadData])

  return {
    languages,
    categories,
    questions,
    users, // Return users
    adminStats,
    loading,
    error,
    getQuestionById,
    getUserById, // Return getUserById
    createCategory,
    createQuestion,
    updateCategory,
    uploadCategoryPhoto,
    updateQuestion,
    updateUser, // Return updateUser
    deleteCategory,
    // deleteQuestion,
    deleteUser, // Return deleteUser
    // genQuestion,
    refreshData,
  }
}

// Authentication hook
export function useAuth() {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setIsAuthenticated(api.auth.isAuthenticated())
    setLoading(false)
  }, [])

  const login = async (username: string, password: string) => {
    try {
      setLoading(true)
      await api.auth.login({ username, password })
      setIsAuthenticated(true)
      toast.success("Login successful", {
        description: "Welcome back!",
      })
    } catch (err) {
      const errorMessage = err instanceof APIError ? err.message : "Login failed"
      toast.error("Login failed", {
        description: errorMessage,
      })
      throw err
    } finally {
      setLoading(false)
    }
  }

  const logout = async () => {
    try {
      await api.auth.logout()
      setIsAuthenticated(false)
      toast.info("Logged out", {
        description: "You have been successfully logged out.",
      })
    } catch (err) {
      // Even if logout fails on server, clear local state
      setIsAuthenticated(false)
      toast.info("Logged out", {
        description: "You have been logged out.",
      })
    }
  }

  return {
    isAuthenticated,
    loading,
    login,
    logout,
  }
}
